import { ActionIcon, Group, Table, Text, Tooltip } from '@mantine/core';
import { closeAllModals, openModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { IconPencil } from '@tabler/icons-react';
import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useNotIdFAQsMethods } from '@mygpt/app';
import { MsgCitation, SessionInfo } from '@mygpt/types';

import { FaqsItemEdit, FaqsItemEditProps } from '@/components/Faqs/item-edit';
import { RatingData, RatingItem } from '@/components/TeachModel/FeedbackTable';

import classes from './table.module.css';
import { useBot } from '@mygpt/app/bot';
import { cleanAgentContent } from '@mygpt/app/utils/agent';

interface UnansweredTableProps {
  data: Array<UnansweredTableItem>;
  actionCallBack?: () => void;
}

export interface UnansweredTableItem {
  id: string;
  faqId: string | null;
  citation: MsgCitation;
  time: number;
  question: string;
  answer: string;
  rating: RatingData[];
  detail: string;
  session: SessionInfo;
}

const FeedbackTable: React.FC<UnansweredTableProps> = ({ actionCallBack, data }) => {
  const { t } = useTranslation();

  const { addFAQs, updateFAQs } = useNotIdFAQsMethods();
  const { robotInfo } = useBot();

  const TypeData = [
    { value: 'all', label: t('chat_history.source_type.all') },
    { value: 'playground', label: t('chat_history.source_type.playground') },
    { value: 'share', label: t('chat_history.source_type.share') },
    { value: 'widget', label: t('chat_history.source_type.widget') },
    { value: 'api', label: t('chat_history.source_type.api') },
    { value: 'realtime', label: t('chat_history.source_type.realtime') },
  ];

  const getSourceLabel = (source: string) => {
    return TypeData.find((_) => _.value === source)?.label;
  };

  const editFaq = (item: UnansweredTableItem) => {
    const isEdit = !!item.faqId;
    const onSubmitEditFaq = async (params: Parameters<NonNullable<FaqsItemEditProps['onSubmit']>>[0]) => {
      const datasetId = params.datasetId || '';

      const successTip = async () => {
        showNotification({
          message: t('save_success'),
          color: 'green.5',
        });

        // 如果是新增就重新刷新数据获取faqId
        if (!item.faqId) {
          actionCallBack && (await actionCallBack());
        }
      };

      if (item.faqId) {
        await updateFAQs({ datasetId, faqsId: item.faqId || '', ...params }).then(() => {
          successTip();
        });
      } else {
        await addFAQs({ datasetId, ...params, session_message_id: item.id }).then((res) => {
          console.log(res);
          successTip();
        });
      }
      closeAllModals();
    };

    // 清理答案中的 agent 特殊标签（如 [TOOL_CALL]、[TOOL_RESPONSE] 等）
    const cleanedAnswer = cleanAgentContent(item.answer || '');

    const faqs = {
      id: 'id',
      created_at: new Date().toISOString(),
      user_id: item.id,
      question: item.question,
      answer: cleanedAnswer,
      rating: 0,
      sources: '',
      updated_at: '',
    };

    openModal({
      title: isEdit ? t('faqs.edit_title') : t('faqs.add_title'),
      withinPortal: false,
      centered: true,
      radius: 'md',
      size: 'lg',
      children: (
        <FaqsItemEdit
          selectDatasetId
          datasetId={''}
          faqs={faqs}
          faqId={item.faqId}
          citation={item.citation}
          onSubmit={onSubmitEditFaq}
          onSubmitText={isEdit ? t('save') : t('faqs.action.add')}
        />
      ),
    });
  };

  return (
    <div>
      <Table className={classes.table}>
        <Table.Thead className="bg-gray-300">
          <Table.Tr>
            <Table.Th>{t('chat_history.table.last_conv_time')}</Table.Th>
            <Table.Th>{t('chat_history.table.title')}</Table.Th>
            <Table.Th>{t('chat_history.table.type')}</Table.Th>
            <Table.Th>{t('teach_model.table.question')}</Table.Th>
            <Table.Th>{t('teach_model.table.rating')}</Table.Th>
            <Table.Th>{t('teach_model.table.feedback_details')}</Table.Th>
            <Table.Th>{t('teach_model.table.user')}</Table.Th>
            <Table.Th>{t('teach_model.table.action')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {data.map((item) => (
            <Table.Tr key={item.id}>
              <Table.Td>
                <Text c="gray.4" size="sm">
                  {dayjs(item.time).format('YYYY-MM-DD HH:mm:ss')}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm">
                  {item.session.title}
                  {item.session.title.length === 20 && `...`}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm">{getSourceLabel(item.session.source) || item.session.source}</Text>
              </Table.Td>
              <Table.Td style={{ maxWidth: '20vw' }}>
                <Group gap={10} wrap="nowrap">
                  <span className="bg-gray-500 py-1 px-2 text-white text-sm rounded-md">Q</span>
                  <Tooltip disabled={!item.question} label={item.question} multiline maw={'50vw'}>
                    <Text size="sm" fw={500} lh={1.2} lineClamp={2}>
                      {item.question}
                    </Text>
                  </Tooltip>
                </Group>
              </Table.Td>
              <Table.Td>
                {item.rating.length
                  ? item.rating.map((item, index) => {
                      return <RatingItem key={index} data={item} />;
                    })
                  : '-'}
              </Table.Td>
              <Table.Td style={{ maxWidth: '30vw' }}>
                <Tooltip
                  disabled={!item.detail}
                  label={<span style={{ wordWrap: 'break-word' }}>{item.detail}</span>}
                  multiline
                  maw={'50vw'}
                >
                  <Text size="sm" lineClamp={2}>
                    {item.detail || '-'}
                  </Text>
                </Tooltip>
              </Table.Td>
              <Table.Td>{item.session.user_identifier}</Table.Td>
              <Table.Td>
                <ActionIcon variant="subtle" onClick={() => editFaq(item)} size="sm">
                  <IconPencil size={14} />
                </ActionIcon>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </div>
  );
};

export default FeedbackTable;
